import React, { useContext } from "react";

export interface IEnvContext
	extends eXpress.api.IServicesContext,
		eXpress.api.IClientContext,
		eXpress.core.IEditorContext,
		eXpress.importer.IDownloadContext,
		eXpress.api.IUploadContext {
	applicationVersion: string;
}

// Load semi hard coded context to be able to work without Razor
function getContext() {
	// env variables should be set with webpack define plugin.
	const cdnUrl = process.env.PUBLIC_CDN_URL || "https://static-dev.stgr.cloud";
	const homeUrl = process.env.PUBLIC_HOME_URL || "https://smartphoto-be-dev.stgr.cloud/";
	const apiUrl = process.env.PUBLIC_API_URL || "https://smartphoto-be-dev.stgr.cloud/api/v1/";
	const ecomApiUrl = process.env.PUBLIC_ECOM_API_URL || "https://api-dev.stgr.cloud/ecom/v2";
	const osmTileServerUrl = process.env.PUBLIC_OSM_TILESERVER_URL || "https://maps-dev.stgr.cloud";
	const osmMapboxAccessToken = process.env.PUBLIC_OSM_MAPBOX_ACCESS_TOKEN || "";
	const filesUrl = `${cdnUrl}/products/`;

	/**
	 * Get the base path the this page
	 * Using previous solution with regex give invalid value when querystring contains a cdnurl with /
	 * Remark: Can we detect if no index.html is used and we are already using the base path?
	 */
	function getBasePath() {
		const origin = window.location.origin;
		const pathname = window.location.pathname;

		// Remove the filename if it exists.
		const lastSlashIndex = pathname.lastIndexOf("/");
		const basePath = origin + pathname.substring(0, lastSlashIndex + 1);

		return basePath;
	}

	/** Is from a full build where JS is deployed in subdirector js/creator */
	const isJsDeployedInSubdir = !!document.querySelector("html[data-expressrevision]");

	const context: IEnvContext = {
		// Services Context
		applicationId: eXpress.ApplicationId.fromString(
			process.env.PUBLIC_APPLICATION_ID || "1C148556-FC30-483D-8699-5EB3831027F6",
		),
		applicationVersion: "1.0.0.0",
		applicationHash: "",
		language: "nl",
		apiHostUrl: apiUrl,
		apiEcomUrl: ecomApiUrl,
		osmTileServerUrl,
		osmMapboxAccessToken,
		jsUrl:
			// A feature build, deploys creator JS files in the same location as the page (bucket)
			isJsDeployedInSubdir
				? "/js/creator/"
				: // example url when static creator is hosted from bucket:
				  // https://storage.googleapis.com/smartphoto-express-qa-mc/builds/SHA/js/creator/index.html?pvc=PhotobookLLHardPers&des=BigLandscape_Black&screen=feature
				  getBasePath(),
		pointOfSaleId: eXpress.GuidString.fromString(
			process.env.PUBLIC_POINT_OF_SALE_ID || "8889361a-c9e5-4cbd-a5a3-bbf7612dd370",
		),
		account: {
			// Temporary? We should use new eXpress.client.Account()
			id: eXpress.AccountId.empty,
			shoppingCartId: eXpress.GuidString.empty,
			name: "Fake account",
			type: eXpress.core.AccountType.Full,
		},

		// Client Context
		productVersion: "637184108516123674",
		cdn: cdnUrl,
		logosRepository: `${filesUrl}logos/default/`,

		// Editor Context
		isDebugBuild: process.env.NODE_ENV !== "production",

		// Download Context
		photoSelectionUrl: "",
		locatePhotosFacebook: "",
		locatePhotosInstagram: "",
		locatePhotosPicasa: "",
		locatePhotosSwisscom: "",
		homeUrl,
		mySavedProductsUrl: "",

		// Upload Context
		revision: process.env.PUBLIC_VERSION,
		maxConcurrentPreprocessings: 2,
	};
	return context;
}

/** Create the value for the React AppContext */
declare let webContext: undefined | IEnvContext;
function createContextValue(): IEnvContext {
	const contextValue = getContext();
	if (typeof webContext === "undefined") {
		return contextValue;
	}

	return {
		...contextValue,
		...webContext,
	};
}

// Create Context
const EnvContext = React.createContext<IEnvContext>({} as IEnvContext);
EnvContext.displayName = "EnvContext";

/** Check to overrule Context values */
function overRuleContext(context: IEnvContext): IEnvContext {
	// Overruled from QueryString: cdnurl? (Disable for builds used live?)
	const cdnFromQueryString = eXpress.platform.queryString.getQueryStringParameter("cdnurl");
	if (cdnFromQueryString && (context.isDebugBuild || context.homeUrl.includes("stgr.cloud"))) {
		context.jsUrl = `${cdnFromQueryString}/js/creator/`;
		return context;
	}

	// Overruled from Cookie: CreatorCdnUrl? (Disable for builds used live?)
	const cdnFromCookie = eXpress.platform.cookies.getCookie("CreatorCdnUrl");
	if (cdnFromCookie && (context.isDebugBuild || context.homeUrl.includes("stgr.cloud")))
		context.jsUrl = `${cdnFromCookie}/js/creator/`;

	return context;
}

/**
 * Component that provides a default AppContext and
 * the possibility to adjust default after creation for testing
 */
export function EnvContextProvider(
	props: React.PropsWithChildren<{
		context?: IEnvContext;
	}>,
) {
	const { children, context: initialContext } = props;

	// Create React AppContext value
	let contextValue = (initialContext as IEnvContext) || createContextValue();

	contextValue = overRuleContext(contextValue);

	return <EnvContext.Provider value={contextValue}>{children}</EnvContext.Provider>;
}

/** Create Hook to consumer Context */
export const useEnvContext = () => useContext(EnvContext);
